import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "./DashboardLayout";
import { AuthenticatedMedia } from "./AuthenticatedMedia";
import { deleteVolunteer, fetchVolunteers, updateVolunteer, VolunteerRow } from "./api";
import ActionToast, { ToastAction } from "./ActionToast";
import "./volunteers.css";

const GENDER_OPTIONS = ["Male", "Female", "Other"];
const SKILL_OPTIONS = ["Technical", "Teaching", "IT", "Others"];
const AVAILABILITY_OPTIONS = ["DAY", "Hours", "Weekend"];
const newestVolunteersFirst = (volunteers: VolunteerRow[]) => [...volunteers].sort((left, right) => Number(right.id || 0) - Number(left.id || 0));
const VOLUNTEERING_AREA_OPTIONS = [
  "Education",
  "Women Empowerment",
  "Environment",
  "Health",
  "Livelihood",
  "Fundraising",
  "Administrator",
];
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const VolunteerAvatar: React.FC<{ src?: string; name: string; initials: string }> = ({ src, name, initials }) => {
  const [failed, setFailed] = useState(false);
  return src && !failed
    ? <img className="volunteer-avatar" src={src} alt={name || "Volunteer"} onError={() => setFailed(true)} />
    : <span className="volunteer-avatar volunteer-initials" aria-label="No volunteer photo">{initials || "-"}</span>;
};

const VolunteerList: React.FC = () => {
  const [rows, setRows] = useState<VolunteerRow[]>([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const [viewRow, setViewRow] = useState<VolunteerRow | null>(null);
  const [editRow, setEditRow] = useState<VolunteerRow | null>(null);
  const [editSkills, setEditSkills] = useState<string[]>([]);
  const [showStateOptions, setShowStateOptions] = useState(false);
  const [stateSearchValue, setStateSearchValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoDragOver, setPhotoDragOver] = useState(false);
  const [toastAction, setToastAction] = useState<ToastAction | null>(null);

  const activeDialog = viewRow ? "view" : editRow ? "edit" : null;
  const loadVolunteers = useCallback(async () => {
    setLoading(true);
    setError("");
    try { setRows(newestVolunteersFirst(await fetchVolunteers())); }
    catch (err: any) { setError(err.message || "Failed to fetch volunteers"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void loadVolunteers(); }, [loadVolunteers]);
  useEffect(() => {
    if (!activeDialog) return;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { setViewRow(null); setEditRow(null); }
      if (event.key === "Tab" && dialogRef.current) {
        const controls = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'));
        if (!controls.length) { event.preventDefault(); return; }
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [activeDialog]);
  useEffect(() => { if (!activeDialog) returnFocusRef.current?.focus(); }, [activeDialog]);

  useEffect(() => {
    if (!toastAction) return;
    const timer = setTimeout(() => setToastAction(null), 6500);
    return () => clearTimeout(timer);
  }, [toastAction]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.fullName?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.skills?.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const getInitials = (name: string) =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("");

  const normalizeSkills = (raw?: string) => {
    if (!raw) return "";
    const value = String(raw).trim();
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean).join(", ");
      }
    } catch {
      // fall through to text normalization
    }
    return value
      .replace(/^\[/, "")
      .replace(/\]$/, "")
      .replace(/"/g, "")
      .replace(/'/g, "")
      .trim();
  };

  const formatDisplayDate = (value?: string) => {
    if (!value) return "-";
    const raw = String(value).trim();

    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      return raw;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [year, month, day] = raw.split("-");
      return `${day}-${month}-${year}`;
    }

    if (raw.includes("T")) {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return raw;
      return d.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata" });
    }

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear());
    return `${day}-${month}-${year}`;
  };

  const toInputDate = (value?: string) => {
    if (!value) return "";
    const raw = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split("-");
      return `${year}-${month}-${day}`;
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const parseSkillsToArray = (raw?: string) => {
    if (!raw) return [];
    const text = String(raw).trim();
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch {
      // fall through
    }
    return text
      .replace(/^\[/, "")
      .replace(/\]$/, "")
      .replace(/"/g, "")
      .replace(/'/g, "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const normalizeFromOptions = (value: string | undefined, options: string[]) => {
    const raw = (value || "").trim();
    if (!raw) return "";
    const found = options.find((option) => option.toLowerCase() === raw.toLowerCase());
    return found || raw;
  };

  const stateOptions = useMemo(() => {
    const merged = [...INDIAN_STATES, ...rows.map((r) => (r.state || "").trim()), (editRow?.state || "").trim()].filter(Boolean);
    return Array.from(new Set(merged)).sort((a, b) => a.localeCompare(b));
  }, [rows, editRow?.state]);

  const filteredStateOptions = useMemo(() => {
    const q = stateSearchValue.trim().toLowerCase();
    if (!q) return stateOptions;
    return stateOptions.filter((option) => option.toLowerCase().includes(q));
  }, [stateOptions, stateSearchValue]);

  const skillOptions = useMemo(() => {
    const merged = [...SKILL_OPTIONS, ...editSkills].filter(Boolean);
    return Array.from(new Set(merged));
  }, [editSkills]);

  const onDelete = async (id: number) => {
    if (!window.confirm("Permanently delete this volunteer's database record? This cannot be undone.")) return;
    try {
      await deleteVolunteer(id);
      setRows((prev) => prev.filter((row) => row.id !== id));
      setToastAction("deleted");
    } catch (err: any) {
      alert(err.message || "Delete failed");
    }
  };

  const onSave = async () => {
    if (!editRow) return;
    const hasValidState = stateOptions.some((option) => option.toLowerCase() === (editRow.state || "").trim().toLowerCase());
    if (!hasValidState) {
      alert("Please select a valid state from the dropdown.");
      return;
    }
    if (editSkills.length === 0) {
      alert("Please select at least one skill.");
      return;
    }
    const hasOthersSkill = editSkills.some((skill) => skill.toLowerCase() === "others");
    const trimmedOtherSkill = (editRow.otherSkill || "").trim();
    if (hasOthersSkill && !trimmedOtherSkill) {
      alert("Please specify other skill.");
      return;
    }

    const rowToSave: VolunteerRow = {
      ...editRow,
      skills: JSON.stringify(editSkills),
      otherSkill: hasOthersSkill ? trimmedOtherSkill : "",
    };

    setSaving(true);
    try {
      const result = await updateVolunteer(rowToSave);
      if (result.photoUrl === undefined) {
        await loadVolunteers();
      } else {
        const updatedRow = { ...rowToSave };
        delete updatedRow.photoFile;
        setRows((prev) => prev.map((item) => item.id === rowToSave.id
          ? { ...item, ...updatedRow, photoUrl: result.photoUrl || undefined } : item));
      }
      setEditRow(null);
      setEditSkills([]);
      setStateSearchValue("");
      setShowStateOptions(false);
      setToastAction("saved");
    } catch (err: any) {
      alert(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="volunteers-page">
      <ActionToast action={toastAction} />
      <h1 className="list-title">Volunteers</h1>
      <p className="list-subtitle">{loading ? "Loading volunteers…" : error ? "Volunteers unavailable" : `${rows.length} registered volunteers`}</p>
      {error && <div className="volunteers-error" role="alert">{error} <button type="button" onClick={loadVolunteers}>Retry</button></div>}
      {!loading && !error && <>
      <div className="list-toolbar">
        <div className="search-wrap">
          <label htmlFor="volunteers-search">Search</label>
          <i className="fa-solid fa-magnifying-glass search-icon" />
          <input
            className="form-control search-input search-input-medium"
            id="volunteers-search"
            placeholder="Search by name, email, or skill..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      <div className="table-shell table-responsive">
        <table className="table data-table mb-0">
          <thead>
            <tr>
              <th style={{ width: 70 }} />
              <th>Name</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>Skills</th>
              <th>Availability</th>
              <th>City/State</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id}>
                <td>
                  <span className="table-avatar">{getInitials(row.fullName || "")}</span>
                </td>
                <td>{row.fullName}</td>
                <td>{row.mobile}</td>
                <td>{row.email}</td>
                <td>
                  <div className="pill-wrap">
                    {normalizeSkills(row.skills)
                      .split(",")
                      .map((skill) => skill.trim())
                      .filter(Boolean)
                      .map((skill) => (
                        <span className="data-pill" key={`${row.id}-${skill}`}>
                          {skill}
                        </span>
                      ))}
                  </div>
                </td>
                <td>
                  <span className="availability-pill">{row.availability || "-"}</span>
                </td>
                <td>
                  {row.city}, {row.state}
                </td>
                <td className="text-end">
                  <div className="action-icons">
                    <button type="button" className="icon-btn" title="View Details" aria-label={`View ${row.fullName}`} onClick={(event) => { returnFocusRef.current = event.currentTarget; setViewRow(row); }}>
                      <i className="fa-regular fa-eye" />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      title="Edit"
                      aria-label={`Edit ${row.fullName}`}
                      onClick={(event) => {
                        returnFocusRef.current = event.currentTarget;
                        setEditRow({ ...row, skills: normalizeSkills(row.skills) });
                        setEditSkills(parseSkillsToArray(row.skills));
                        setStateSearchValue(row.state || "");
                        setShowStateOptions(false);
                      }}
                    >
                      <i className="fa-regular fa-pen-to-square" />
                    </button>
                    <button type="button" className="icon-btn icon-btn-danger" title="Delete" aria-label={`Delete ${row.fullName}`} onClick={() => onDelete(row.id)}>
                      <i className="fa-regular fa-trash-can" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-4 text-muted">
                  No volunteers found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </>}

      {viewRow && (
        <div className="dash-modal-overlay" onClick={() => setViewRow(null)}>
          <div className="dash-modal" role="dialog" aria-modal="true" aria-labelledby="volunteer-view-title" tabIndex={-1} ref={dialogRef} onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 id="volunteer-view-title" className="dash-modal-title">Volunteer Details</h3>
              <button type="button" className="dash-modal-close" aria-label="Close volunteer details" onClick={() => setViewRow(null)}>x</button>
            </div>
            <div className="dash-modal-body volunteer-profile">
              <div className="volunteer-profile-header">
                {viewRow.photoUrl ? <AuthenticatedMedia endpoint={`/files/volunteers/${viewRow.id}/photo`} alt={viewRow.fullName || "Volunteer"} className="volunteer-avatar" fallback={<VolunteerAvatar name={viewRow.fullName || ""} initials={getInitials(viewRow.fullName || "")} />} /> : <VolunteerAvatar name={viewRow.fullName || ""} initials={getInitials(viewRow.fullName || "")} />}
                <div className="volunteer-profile-identity"><h4>{viewRow.fullName || "-"}</h4><p>Volunteer ID: {viewRow.volunteerId || "-"}</p></div>
              </div>
              <section className="volunteer-profile-section"><h4>Contact</h4><dl>
                <div><dt>Mobile</dt><dd>{viewRow.mobile || "-"}</dd></div><div><dt>Email</dt><dd>{viewRow.email || "-"}</dd></div>
              </dl></section>
              <section className="volunteer-profile-section"><h4>Personal Information</h4><dl>
                <div><dt>Aadhar (Optional)</dt><dd>{viewRow.aadhar || "-"}</dd></div><div><dt>Gender</dt><dd>{viewRow.gender || "-"}</dd></div><div><dt>Date of Birth</dt><dd>{formatDisplayDate(viewRow.dateOfBirth)}</dd></div>
              </dl></section>
              <section className="volunteer-profile-section"><h4>Address</h4><dl>
                <div><dt>State</dt><dd>{viewRow.state || "-"}</dd></div><div><dt>City</dt><dd>{viewRow.city || "-"}</dd></div><div><dt>Pin Code</dt><dd>{viewRow.pinCode || "-"}</dd></div><div className="volunteer-profile-wide"><dt>Address</dt><dd>{viewRow.address || "-"}</dd></div>
              </dl></section>
              <section className="volunteer-profile-section"><h4>Volunteering</h4><dl>
                <div><dt>Skills &amp; Expertise</dt><dd>{normalizeSkills(viewRow.skills) || "-"}</dd></div><div><dt>Other Skill</dt><dd>{viewRow.otherSkill || "-"}</dd></div><div><dt>Availability</dt><dd>{viewRow.availability || "-"}</dd></div><div><dt>Preferred Volunteering Area</dt><dd>{viewRow.volunteeringArea || "-"}</dd></div><div><dt>Previous Volunteer Experience</dt><dd>{viewRow.previousExperience || "-"}</dd></div><div className="volunteer-profile-wide"><dt>Experience Details</dt><dd>{viewRow.experienceDetails || "-"}</dd></div>
              </dl></section>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="volunteer-button volunteer-secondary" onClick={() => setViewRow(null)}>Close</button>
              <button type="button" className="volunteer-button volunteer-primary" onClick={() => {
                setEditRow({ ...viewRow, skills: normalizeSkills(viewRow.skills) });
                setEditSkills(parseSkillsToArray(viewRow.skills));
                setStateSearchValue(viewRow.state || "");
                setShowStateOptions(false);
                setViewRow(null);
              }}>Edit Volunteer</button>
            </div>
          </div>
        </div>
      )}

      {editRow && (
        <div className="dash-modal-overlay" onClick={() => setEditRow(null)}>
          <div className="dash-modal" role="dialog" aria-modal="true" aria-labelledby="volunteer-edit-title" tabIndex={-1} ref={dialogRef} onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 id="volunteer-edit-title" className="dash-modal-title">Edit Volunteer</h3>
              <button type="button" className="dash-modal-close" aria-label="Close edit volunteer" onClick={() => setEditRow(null)}>
                x
              </button>
            </div>
            <div className="dash-modal-body">
              <div className="edit-grid">
                <div className="edit-field">
                  <label className="edit-label" htmlFor="volunteer-edit-0">Full Name</label>
                  <input id="volunteer-edit-0" className="form-control" placeholder="Enter full name" value={editRow.fullName || ""} onChange={(e) => setEditRow({ ...editRow, fullName: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="volunteer-edit-1">Mobile</label>
                  <input id="volunteer-edit-1" className="form-control" placeholder="Enter mobile number" value={editRow.mobile || ""} onChange={(e) => setEditRow({ ...editRow, mobile: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="volunteer-edit-2">Email</label>
                  <input id="volunteer-edit-2" className="form-control" placeholder="Enter email address" value={editRow.email || ""} onChange={(e) => setEditRow({ ...editRow, email: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="volunteer-edit-3">Aadhar (Optional)</label>
                  <input id="volunteer-edit-3" className="form-control" placeholder="Enter Aadhar number" value={editRow.aadhar || ""} onChange={(e) => setEditRow({ ...editRow, aadhar: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="volunteer-edit-4">Gender</label>
                  <select id="volunteer-edit-4" className="form-select" value={normalizeFromOptions(editRow.gender, GENDER_OPTIONS)} onChange={(e) => setEditRow({ ...editRow, gender: e.target.value })}>
                    <option value="">Select Gender</option>
                    {GENDER_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    {normalizeFromOptions(editRow.gender, GENDER_OPTIONS) && !GENDER_OPTIONS.includes(normalizeFromOptions(editRow.gender, GENDER_OPTIONS)) && (
                      <option value={normalizeFromOptions(editRow.gender, GENDER_OPTIONS)}>{normalizeFromOptions(editRow.gender, GENDER_OPTIONS)}</option>
                    )}
                  </select>
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="volunteer-edit-5">Date of Birth</label>
                  <input id="volunteer-edit-5" className="form-control" type="date" placeholder="Select date of birth" value={toInputDate(editRow.dateOfBirth)} onChange={(e) => setEditRow({ ...editRow, dateOfBirth: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="volunteer-edit-6">State</label>
                  <div style={{ position: "relative" }}>
                    <input id="volunteer-edit-6"
                      className="form-control"
                      placeholder="Search or select state"
                      value={stateSearchValue}
                      onFocus={() => setShowStateOptions(true)}
                      onChange={(e) => {
                        setStateSearchValue(e.target.value);
                        setShowStateOptions(true);
                      }}
                      onBlur={() => {
                        const matched = stateOptions.find((option) => option.toLowerCase() === stateSearchValue.trim().toLowerCase());
                        if (matched) {
                          setStateSearchValue(matched);
                          setEditRow({ ...editRow, state: matched });
                        } else {
                          setStateSearchValue(editRow.state || "");
                        }
                        setTimeout(() => setShowStateOptions(false), 120);
                      }}
                    />
                    {showStateOptions && filteredStateOptions.length > 0 && (
                      <div
                        className="list-group"
                        style={{
                          position: "absolute",
                          zIndex: 20,
                          width: "100%",
                          maxHeight: "180px",
                          overflowY: "auto",
                          border: "1px solid #dfe5ef",
                          borderRadius: "8px",
                          marginTop: "6px",
                          background: "#fff",
                        }}
                      >
                        {filteredStateOptions.slice(0, 50).map((option) => (
                          <button
                            key={option}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onMouseDown={(evt) => evt.preventDefault()}
                            onClick={() => {
                              setStateSearchValue(option);
                              setEditRow({ ...editRow, state: option });
                              setShowStateOptions(false);
                            }}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="volunteer-edit-7">City</label>
                  <input id="volunteer-edit-7" className="form-control" placeholder="Enter city" value={editRow.city || ""} onChange={(e) => setEditRow({ ...editRow, city: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="volunteer-edit-8">Pin Code</label>
                  <input id="volunteer-edit-8" className="form-control" placeholder="Enter pin code" value={editRow.pinCode || ""} onChange={(e) => setEditRow({ ...editRow, pinCode: e.target.value })} />
                </div>
                <div className="edit-field full">
                  <label className="edit-label" htmlFor="volunteer-edit-9">Address</label>
                  <input id="volunteer-edit-9" className="form-control" placeholder="Enter full address" value={editRow.address || ""} onChange={(e) => setEditRow({ ...editRow, address: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="volunteer-edit-10">Skills & Expertise</label>
                  <select id="volunteer-edit-10"
                    className="form-select"
                    multiple
                    value={editSkills}
                    onChange={(e) => {
                      const values = Array.from(e.currentTarget.selectedOptions, (option) => option.value);
                      setEditSkills(values);
                      if (!values.includes("Others")) {
                        setEditRow({ ...editRow, otherSkill: "" });
                      }
                    }}
                  >
                    {skillOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {editSkills.includes("Others") && (
                    <input
                      className="form-control mt-2"
                      aria-label="Specify other skill"
                      placeholder="Specify other skill"
                      value={editRow.otherSkill || ""}
                      onChange={(e) => setEditRow({ ...editRow, otherSkill: e.target.value })}
                    />
                  )}
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="volunteer-edit-11">Availability</label>
                  <select id="volunteer-edit-11" className="form-select" value={normalizeFromOptions(editRow.availability, AVAILABILITY_OPTIONS)} onChange={(e) => setEditRow({ ...editRow, availability: e.target.value })}>
                    <option value="">Select Availability</option>
                    {AVAILABILITY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option === "DAY" ? "Day" : option}
                      </option>
                    ))}
                    {normalizeFromOptions(editRow.availability, AVAILABILITY_OPTIONS) &&
                      !AVAILABILITY_OPTIONS.includes(normalizeFromOptions(editRow.availability, AVAILABILITY_OPTIONS)) && (
                        <option value={normalizeFromOptions(editRow.availability, AVAILABILITY_OPTIONS)}>
                          {normalizeFromOptions(editRow.availability, AVAILABILITY_OPTIONS)}
                        </option>
                      )}
                  </select>
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="volunteer-edit-12">Preferred Volunteering Area</label>
                  <select id="volunteer-edit-12" className="form-select" value={normalizeFromOptions(editRow.volunteeringArea, VOLUNTEERING_AREA_OPTIONS)} onChange={(e) => setEditRow({ ...editRow, volunteeringArea: e.target.value })}>
                    <option value="">Select Area</option>
                    {VOLUNTEERING_AREA_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    {normalizeFromOptions(editRow.volunteeringArea, VOLUNTEERING_AREA_OPTIONS) &&
                      !VOLUNTEERING_AREA_OPTIONS.includes(normalizeFromOptions(editRow.volunteeringArea, VOLUNTEERING_AREA_OPTIONS)) && (
                        <option value={normalizeFromOptions(editRow.volunteeringArea, VOLUNTEERING_AREA_OPTIONS)}>
                          {normalizeFromOptions(editRow.volunteeringArea, VOLUNTEERING_AREA_OPTIONS)}
                        </option>
                      )}
                  </select>
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="volunteer-edit-13">Previous Volunteer Experience</label>
                  <input id="volunteer-edit-13" className="form-control" placeholder="Enter previous experience" value={editRow.previousExperience || ""} onChange={(e) => setEditRow({ ...editRow, previousExperience: e.target.value })} />
                </div>
                <div className="edit-field full">
                  <label className="edit-label">Photo Upload</label>
                  {editRow.photoUrl && !editRow.photoFile && <div className="mb-2"><AuthenticatedMedia endpoint={`/files/volunteers/${editRow.id}/photo`} alt="Existing volunteer photo" className="volunteer-avatar" fallback={<span className="text-muted">Existing photo unavailable</span>} /></div>}
                  {editRow.photoFile && <div className="text-muted mb-2">Replacement selected: <strong>{editRow.photoFile.name}</strong></div>}
                  <label
                    className={`dash-upload-box ${photoDragOver ? "is-dragover" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setPhotoDragOver(true);
                    }}
                    onDragLeave={() => setPhotoDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setPhotoDragOver(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) setEditRow({ ...editRow, photoFile: file });
                    }}
                  >
                    <i className="fa-regular fa-image" />
                    <span>JPEG, PNG or WebP</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      aria-label="Photo Upload (JPEG, PNG or WebP)"
                      className="dash-upload-input"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setEditRow({ ...editRow, photoFile: file });
                      }}
                    />
                  </label>
                </div>
                <div className="edit-field full">
                  <label className="edit-label" htmlFor="volunteer-edit-14">Experience Details</label>
                  <textarea id="volunteer-edit-14" className="form-control" rows={3} placeholder="Enter experience details" value={editRow.experienceDetails || ""} onChange={(e) => setEditRow({ ...editRow, experienceDetails: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setEditRow(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" disabled={saving} onClick={onSave}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </DashboardLayout>
  );
};

export default VolunteerList;
